/**
 * AccuWeather ID Graph Builder
 * -----------------------------------------------------------------------------
 * Resolves up to 7 user IDs and applies them to ortb2.user.eids and
 * ortb2.user.ext.eids via pbjs.setConfig() before requestBids().
 *
 * Prebid compatibility: tested against Prebid 9.x, compatible with Prebid 10.
 *   - Uses getUserIdsAsEids() only — bid.userId and getUserIds() not referenced
 *   - Writes to both ortb2.user.eids and ortb2.user.ext.eids for full
 *     compatibility: Prebid 9.x adapters read ext.eids; Prebid 10 syncs them
 *     automatically but the explicit write is harmless and can stay post-upgrade
 *   - Not affected by retainConfig (default true in Prebid 10) — this file
 *     does not manage userId module config, only reads resolved EIDs
 *   - Main Prebid config should set userSync.autoRefresh: true and verify
 *     storage.type matches actual storage for each userId module
 *     (enforceStorageType: currently warns in Prebid 10, will block in future)
 *
 * Structure:
 *   Fixed (always, in this order):
 *     1. uidapi.com      — UID2
 *     2. id5-sync.com    — ID5
 *
 *   Randomized pool — 5 picked per page load from 8 candidates:
 *     adserver.org       — TDID (TTD Unified ID)
 *     pubcid.org         — SharedID (sharedIdSystem writes to _pubcid cookie)
 *     criteo.com         — Criteo encrypted bundle
 *     crwdcntrl.net      — Lotame Panorama
 *     audigent.com       — Audigent HAM
 *     33across.com       — 33Across ID
 *     sharethrough.com   — Sharethrough (_swb cookie)
 *     gumgum.com         — GumGum GEUID
 *
 * Randomizing the pool distributes identity signal across DSPs that don't
 * bid on every impression, improving match rate coverage over time without
 * bloating any single request. Pool slots are re-sorted by original index
 * after shuffle so relative priority is preserved when all are filled.
 *
 * UID2 rule:
 *   Logged-in (~1%): uid2IdSystem handles CSTG natively — token already
 *   in getUserIdsAsEids(), picked up via passthrough. No interference.
 *   Anonymous (~99%): fill from storage in order:
 *     OPTABLE_TC → __uid2_advertising_token → _GESPSK-uidapi.com → cnx
 *   No refresh — we do not own the operator credentials.
 *   Future: write server-side tokens to __uid2_advertising_token.
 *
 * Temporary Optable sources (remove when Longitude/Optable is replaced):
 *   - OPTABLE_TC in UID2 fallback chain
 *   - _GESPSK-uidapi.com in UID2 fallback chain
 *
 */

export default function buildIdGraph() {
	// ---------------------------------------------------------------------------
	// Helpers
	// ---------------------------------------------------------------------------

	const DEBUG = window.location.hash.indexOf('idgraph') > -1;
	const _log = function () { if (DEBUG) console.log.apply(console, ['[AWX ID Graph]'].concat(Array.prototype.slice.call(arguments))); };

	const tryParse = (v) => {
		if (!v) return null;
		try { return JSON.parse(v); } catch (_) { return null; }
	};

	const getCookie = (name) => {
		const m = document.cookie.split('; ').find(r => r.startsWith(name + '='));
		if (!m) return null;
		try { return decodeURIComponent(m.split('=').slice(1).join('=')); } catch (_) { return null; }
	};

	const getLs = (key) => {
		try { return localStorage.getItem(key); } catch (_) { return null; }
	};

	const now = Date.now();

	// Fisher-Yates shuffle
	const shuffle = (arr) => {
		const a = [...arr];
		for (let i = a.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[a[i], a[j]] = [a[j], a[i]];
		}
		return a;
	};

	// ---------------------------------------------------------------------------
	// Slot definition
	// ---------------------------------------------------------------------------

	const FIXED = [
		'uidapi.com',
		'id5-sync.com',
	];

	const POOL = [
		'adserver.org',
		'pubcid.org',
		'criteo.com',
		'crwdcntrl.net',
		'audigent.com',
		'33across.com',
		'sharethrough.com',
		'gumgum.com',
	];

	const POOL_PICK = 5;
	const MAX_EIDS = FIXED.length + POOL_PICK; // 7

	// Shuffle pool, pick POOL_PICK, re-sort by original index to preserve
	// relative priority when multiple picked slots resolve
	const pickedPool = shuffle(POOL)
		.slice(0, POOL_PICK)
		.sort((a, b) => POOL.indexOf(a) - POOL.indexOf(b));

	const ACTIVE = [...FIXED, ...pickedPool];

	// EID map — first writer wins per source
	const eidMap = new Map();

	const addEid = (source, uids, ext = {}) => {
		if (!ACTIVE.includes(source)) return false;
		if (eidMap.has(source)) return false;
		if (!uids?.length) return false;
		if (uids.some(u => !u.id || String(u.id).trim() === '' ||
			String(u.id).startsWith('null'))) return false;
		const eid = { source, uids };
		if (Object.keys(ext).length > 0) eid.ext = ext;
		eidMap.set(source, eid);
		return true;
	};

	// ---------------------------------------------------------------------------
	// Fixed slot 1 — UID2 (uidapi.com)
	// ---------------------------------------------------------------------------

	const uid2AlreadyResolved = (() => {
		try {
			return window.pbjs?.getUserIdsAsEids?.()?.some(e => e.source === 'uidapi.com') || false;
		} catch (_) { return false; }
	})();

	if (uid2AlreadyResolved) {
		_log('UID2: uid2IdSystem resolved — passthrough will handle');
	} else {
		let uid2Token = null;

		// 1a. OPTABLE_TC — temporary, remove when Optable is gone
		const optableTC = tryParse(getLs('OPTABLE_TC'));
		if (optableTC?.advertising_token && (optableTC.identity_expires ?? 0) > now) {
			uid2Token = optableTC.advertising_token;
		}

		// 1b. Standard uid2IdSystem storage (future server-side writes go here)
		if (!uid2Token) {
			const std = tryParse(getLs('__uid2_advertising_token')) ??
				tryParse(getCookie('__uid2_advertising_token'));
			if (std?.advertising_token && (std.identity_expires ?? 0) > now) {
				uid2Token = std.advertising_token;
			}
		}

		// 1c. ID5 GESPSK cache — temporary, remove when Optable is gone
		if (!uid2Token) {
			const gespsk = tryParse(getLs('_GESPSK-uidapi.com'));
			if (Array.isArray(gespsk) && gespsk[1]?.startsWith('A4AAAA')) {
				uid2Token = gespsk[1];
			}
		}

		// 1d. Connatix supplemental fallback
		if (!uid2Token) {
			const cnx = tryParse(getLs('cnx_user_ids'));
			const cnxId = cnx?.supplementalEids?.find(e => e.source === 'uidapi.com')?.idList?.[0];
			if (cnxId?.startsWith('A4AAAA')) uid2Token = cnxId;
		}

		if (uid2Token) addEid('uidapi.com', [{ id: uid2Token, atype: 3 }]);
	}

	// ---------------------------------------------------------------------------
	// Fixed slot 2 — ID5 (id5-sync.com)
	// ---------------------------------------------------------------------------

	const id5Cookie = tryParse(getCookie('id5id'));
	const id5ExpStr = getLs('id5id_exp');
	const id5Expired = id5ExpStr ? new Date(id5ExpStr).getTime() < now : false;
	const id5CookieUid = id5Cookie?.universal_uid ??
		id5Cookie?.ids?.id5id?.eid?.uids?.[0]?.id;

	if (id5CookieUid && !id5Expired) {
		addEid('id5-sync.com', [{
			id: id5CookieUid,
			atype: 1,
			ext: {
				linkType: id5Cookie.link_type ?? id5Cookie.ext?.linkType ?? 2,
				abTestingControlGroup: false
			}
		}]);
	} else {
		// Fallback: best non-expired id5id_v2_* localStorage entry
		let bestUid = null, bestTs = 0;
		try {
			for (let i = 0; i < localStorage.length; i++) {
				const k = localStorage.key(i);
				if (!k.startsWith('id5id_v2_') || k === 'id5id_v2_signature') continue;
				const p = tryParse(getLs(k));
				if (!p?.data?.response?.universal_uid || p.expireAt < now) continue;
				const ts = p.data.responseTimestamp ?? 0;
				if (ts > bestTs) {
					bestTs = ts;
					bestUid = {
						uid: p.data.response.universal_uid,
						linkType: p.data.response.link_type
					};
				}
			}
		} catch (_) { }
		if (bestUid) {
			addEid('id5-sync.com', [{
				id: bestUid.uid,
				atype: 1,
				ext: { linkType: bestUid.linkType ?? 2, abTestingControlGroup: false }
			}]);
		}
	}

	// ---------------------------------------------------------------------------
	// Pool slots — only resolved if selected this page load
	// ---------------------------------------------------------------------------

	// adserver.org — TDID (TTD Unified ID)
	if (ACTIVE.includes('adserver.org')) {
		const unifiedId = tryParse(getCookie('pbjs-unifiedid'));
		if (unifiedId?.TDID) {
			addEid('adserver.org', [{
				id: unifiedId.TDID,
				atype: 1,
				ext: { rtiPartner: 'TDID' }
			}]);
		}
	}

	// pubcid.org — SharedID (sharedIdSystem writes to _pubcid cookie)
	if (ACTIVE.includes('pubcid.org')) {
		const pubcid = getCookie('_pubcid') ?? getLs('_pubcid');
		if (pubcid) addEid('pubcid.org', [{ id: pubcid, atype: 1 }]);
	}

	// criteo.com — Criteo encrypted bundle
	if (ACTIVE.includes('criteo.com')) {
		const ctoBundle = getCookie('cto_bundle');
		if (ctoBundle) {
			addEid('criteo.com', [{
				id: ctoBundle,
				atype: 1,
				ext: { rtiPartner: 'cto_bundle' }
			}]);
		}
	}

	// crwdcntrl.net — Lotame Panorama
	// Primary: panoramaId cookie; fallback: Connatix cnx_user_ids
	if (ACTIVE.includes('crwdcntrl.net')) {
		const panoramaId = getCookie('panoramaId') ?? getLs('panoramaId');
		const panoramaType = getCookie('panoramaIdType') ?? 'panoIndiv';
		if (panoramaId) {
			addEid('crwdcntrl.net', [{
				id: panoramaId,
				atype: panoramaType === 'panoIndiv' ? 1 : 3,
				ext: { idtype: panoramaType }
			}]);
		} else {
			const cnx = tryParse(getLs('cnx_user_ids'));
			const cnxLotame = cnx?.supplementalEids?.find(e => e.source === 'crwdcntrl.net')?.idList?.[0];
			if (cnxLotame) addEid('crwdcntrl.net', [{ id: cnxLotame, atype: 3 }]);
		}
	}

	// audigent.com — Audigent HAM; fallback: _au_1d stable 1p cookie
	if (ACTIVE.includes('audigent.com')) {
		const auEids = tryParse(getLs('auEids'));
		const auHamEid = (auEids?.eids ?? []).find(e => e.source === 'audigent.com');
		if (auHamEid?.uids?.length) {
			const uids = auHamEid.uids.filter(u => u.id).map(u => ({
				id: u.id, ...(u.atype ? { atype: u.atype } : {})
			}));
			addEid('audigent.com', uids);
		} else {
			const au1d = getCookie('_au_1d');
			if (au1d) addEid('audigent.com', [{ id: au1d, atype: 1 }]);
		}
	}

	// 33across.com — read from getUserIdsAsEids() since the module handles it
	if (ACTIVE.includes('33across.com')) {
		const eid33 = window.pbjs?.getUserIdsAsEids?.()?.find(e => e.source === '33across.com') ?? null;
		if (eid33?.uids?.length) {
			const uids = eid33.uids.filter(u => u.id).map(u => ({
				id: u.id, ...(u.atype ? { atype: u.atype } : {})
			}));
			addEid('33across.com', uids);
		}
	}

	// sharethrough.com — _swb first-party cookie
	if (ACTIVE.includes('sharethrough.com')) {
		const swb = getCookie('_swb');
		if (swb) addEid('sharethrough.com', [{ id: swb, atype: 1 }]);
	}

	// gumgum.com — GEUID from localStorage
	if (ACTIVE.includes('gumgum.com')) {
		const geuid = getLs('geuid');
		if (geuid) addEid('gumgum.com', [{ id: geuid, atype: 1 }]);
	}

	// ---------------------------------------------------------------------------
	// Passthrough — capture slots already resolved by Prebid ID modules
	// (e.g. uid2IdSystem for logged-in users). Only fills ACTIVE slots.
	// ---------------------------------------------------------------------------

	try {
		(window.pbjs?.getUserIdsAsEids?.() ?? []).forEach(eid => {
			if (!eid.source || !eid.uids?.length) return;
			const uids = eid.uids.filter(u => u.id).map(u => ({
				id: u.id, ...(u.atype ? { atype: u.atype } : {})
			}));
			addEid(eid.source, uids);
		});
	} catch (_) { }

	// ---------------------------------------------------------------------------
	// Assemble in priority order and apply
	// ---------------------------------------------------------------------------

	const finalEids = ACTIVE
		.filter(s => eidMap.has(s))
		.map(s => eidMap.get(s));

	if (finalEids.length === 0) {
		_log('No EIDs resolved');
		return [];
	}

	let applied = 0;
	try {
		if (window.pbjs?.setConfig && window.pbjs?.getConfig) {
			const existingOrtb2 = window.pbjs?.getConfig?.('ortb2') ?? {};
			window.pbjs.setConfig({
				ortb2: {
					...existingOrtb2,
					user: {
						...(existingOrtb2.user ?? {}),
						eids: finalEids,
						ext: {
							...(existingOrtb2.user?.ext ?? {}),
							eids: finalEids
						}
					}
				}
			});
			applied++;
		}
	} catch (e) {
		_log('setConfig failed on pbjs:', e.message);
	}

	// Google Ad Manager secure signals
	try {
		if (window.googletag?.pubads) {
			const gamEidMap = new Map(finalEids.map(e => [e.source, e]));

			(window.pbjs?.getUserIdsAsEids?.() ?? []).forEach(eid => {
				if (!gamEidMap.has(eid.source) && eid.uids?.length) {
					const uids = eid.uids.filter(u => u.id).map(u => ({
						id: u.id, ...(u.atype ? { atype: u.atype } : {})
					}));

					if (uids.length) {
						gamEidMap.set(eid.source, { source: eid.source, uids });
					}
				}
			});

			const signals = [...gamEidMap.values()].map(eid => ({
				id: eid.source,
				collectorFunction: () => Promise.resolve(eid.uids[0].id)
			}));

			window.googletag.pubads().setEncryptedSignalProviders(signals);
			applied++;
		}
	} catch (e) {
		_log('setEncryptedSignalProviders failed:', e.message);
	}

	// OpenAds/oajs
	try {
		if (window.oajs?.setConfig && window.oajs?.getConfig) {
			const oajsOrtb2 = window.oajs?.getConfig?.('ortb2') ?? {};
			window.oajs.setConfig({
				ortb2: {
					...oajsOrtb2,
					user: {
						...(oajsOrtb2.user ?? {}),
						eids: finalEids,
						ext: {
							...(oajsOrtb2.user?.ext ?? {}),
							eids: finalEids
						}
					}
				}
			});
			applied++;
		}
	} catch (e) {
		_log('setConfig failed on oajs:', e.message);
	}

	_log(
		finalEids.length + '/' + MAX_EIDS + ' slots filled',
		'| pool picked: [' + pickedPool.join(', ') + ']',
		'| instances: ' + applied
	);
	if (DEBUG) {
		console.table(finalEids.map((e, i) => ({
			slot: i + 1,
			source: e.source,
			fixed: FIXED.includes(e.source) ? 'yes' : 'no',
			id_preview: String(e.uids[0].id).substring(0, 48) + '...',
			atype: e.uids[0].atype ?? '-',
		})));
	}

	// Log resolved providers to adInfo for GAM reporting.
	// Fixed order, abbreviated to keep cardinality low.
	var ABBREV_ORDER = [
		{ source: 'uidapi.com', abbr: 'uid2' },
		{ source: 'id5-sync.com', abbr: 'id5' },
		{ source: 'adserver.org', abbr: 'tdid' },
		{ source: 'pubcid.org', abbr: 'shid' },
		{ source: 'criteo.com', abbr: 'crt' },
		{ source: 'crwdcntrl.net', abbr: 'lot' },
		{ source: 'audigent.com', abbr: 'aud' },
		{ source: '33across.com', abbr: '33x' },
		{ source: 'sharethrough.com', abbr: 'str' },
		{ source: 'gumgum.com', abbr: 'gg' }
	];
	var resolvedSources = new Set(finalEids.map(function (e) { return e.source; }));
	var idgraphStr = ABBREV_ORDER
		.filter(function (a) { return resolvedSources.has(a.source); })
		.map(function (a) { return a.abbr; })
		.join(',');
	try {
		window.adInfo = window.adInfo || {};
		window.adInfo.idgraph = idgraphStr || 'none';
	} catch (_) { }

	return finalEids;
};
